import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { purchaseService } from "./purchase.service";

const initialState = {
    purchases: [],
    currentPurchase: null,
    pagination: {},
    loading: false,
    error: null,
    success: false,
    message: "",
};

export const fetchPurchases = createAsyncThunk(
    "purchases/fetchPurchases",
    async (params, { rejectWithValue }) => {
        try {
            const response = await purchaseService.getAllPurchases(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch purchases");
        }
    }
);

export const fetchPurchaseDetails = createAsyncThunk(
    "purchases/fetchPurchaseDetails",
    async (id, { rejectWithValue }) => {
        try {
            const response = await purchaseService.getPurchaseDetails(id);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch purchase details");
        }
    }
);

export const createPurchase = createAsyncThunk(
    "purchases/createPurchase",
    async (purchaseData, { rejectWithValue }) => {
        try {
            const response = await purchaseService.createPurchase(purchaseData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to create purchase");
        }
    }
);

export const addPurchasePayment = createAsyncThunk(
    "purchases/addPayment",
    async ({ id, paymentData }, { rejectWithValue }) => {
        try {
            const response = await purchaseService.addPayment(id, paymentData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to add payment");
        }
    }
);

export const fetchCreditPurchases = createAsyncThunk(
    "purchases/fetchCreditPurchases",
    async (_, { rejectWithValue }) => {
        try {
            const response = await purchaseService.getCreditPurchases();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch credit purchases");
        }
    }
);

const purchaseSlice = createSlice({
    name: "purchases",
    initialState,
    reducers: {
        reset: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
            state.message = "";
        },
        clearCurrentPurchase: (state) => {
            state.currentPurchase = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Purchases
            .addCase(fetchPurchases.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchases.fulfilled, (state, action) => {
                state.loading = false;
                state.purchases = action.payload?.data || action.payload || [];
                state.pagination = action.payload?.pagination || {};
                state.error = null;
            })
            .addCase(fetchPurchases.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.purchases = [];
            })

            // Fetch Purchase Details
            .addCase(fetchPurchaseDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPurchaseDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.currentPurchase = action.payload;
                state.error = null;
            })
            .addCase(fetchPurchaseDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.currentPurchase = null;
            })

            // Create Purchase
            .addCase(createPurchase.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPurchase.fulfilled, (state, action) => {
                state.loading = false;
                state.purchases.unshift(action.payload);
                state.success = true;
                state.message = "Purchase created successfully";
                state.error = null;
            })
            .addCase(createPurchase.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.success = false;
            })

            // Add Payment
            .addCase(addPurchasePayment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addPurchasePayment.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = "Payment added successfully";
                state.error = null;
            })
            .addCase(addPurchasePayment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.success = false;
            })

            // Fetch Credit Purchases
            .addCase(fetchCreditPurchases.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCreditPurchases.fulfilled, (state, action) => {
                state.loading = false;
                state.purchases = action.payload?.data || action.payload || [];
                state.error = null;
            })
            .addCase(fetchCreditPurchases.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { reset, clearCurrentPurchase } = purchaseSlice.actions;
export default purchaseSlice.reducer;
